import { PropsWithChildren, ReactNode, useState } from 'react'
import { Float } from '@headlessui-float/react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { NeutralButton } from 'shared/components/button'
import ButtonLinkWrapper from 'shared/components/button/components/button-link-wrapper'
import { MAX_VISIBLE_BIO_CHARS } from 'shared/constants'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'
import { useActivityRelativeDate } from 'shared/hooks/use-community-relative-date'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import ActivityIcon from 'shared/icons/activity-icon'
import CalendarIcon from 'shared/icons/calendar-icon'
import MailIcon from 'shared/icons/mail-icon'
import { getTimeDifference } from 'shared/utils/date-time/get-time-difference'
import { getContactUrl } from 'shared/utils/url-utils'
import { UserInfoPopoverSkeleton } from 'modules/community/components/user-info-popover/user-info-popover-skeleton'
import useUserInfo from 'modules/community/hooks/use-user-info'
import { getTruncatedText } from './utils/get-truncated-text'

interface UserInfoPopoverProps {
  userId: number
  className?: string
}

export const UserInfoPopover = ({
  children,
  userId,
  className,
}: PropsWithChildren<UserInfoPopoverProps>): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { show, delayClose, delayOpen, close, open } = useFloatHover({ openDelay: 600 })
  const useNativeAnchor = useHeadlessUiAnchor()
  const { data: userInfo } = useUserInfo({ userId, shouldFetch: show })
  const joinedRelative = useActivityRelativeDate(userInfo?.createdAt)
  const lastActiveRelative = useActivityRelativeDate(userInfo?.lastActiveAt)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const isActive = userInfo?.lastActiveAt
    ? // TODO: i think we can come up with better useTimer, and use it here to get timed updates
      // eslint-disable-next-line @eslint-react/purity
      getTimeDifference(Date.now(), userInfo.lastActiveAt, 'minute') <= 5
    : false

  const trigger = (
    <PopoverButton
      onMouseEnter={delayOpen}
      onMouseLeave={delayClose}
      onClick={show ? close : open}
      className="flex h-fit w-full items-center justify-around gap-2.5 focus:outline-hidden"
    >
      {children}
    </PopoverButton>
  )

  const panel = (
    <PopoverPanel
      {...(useNativeAnchor
        ? { static: true, portal: true, anchor: { to: 'bottom' as const, gap: 10 } }
        : {})}
      onMouseEnter={delayOpen}
      onMouseLeave={delayClose}
      className="flex flex-col rounded-lg bg-background-secondary text-sm shadow-lg"
    >
      {userInfo ? (
        <div className="flex size-full flex-col items-start gap-3 px-3 py-5 sm:min-h-[250px] sm:w-[500px] sm:flex-row">
          <img
            src={userInfo.profileImageUrl}
            alt={userInfo.displayName}
            className="size-40 rounded-full"
            width={160}
            height={160}
          />
          <div className="flex w-full grow flex-col gap-3">
            <div className="max-w-[304px] overflow-hidden wrap-break-word text-ellipsis">
              <h3 className="line-clamp-2 max-w-full text-3xl">{userInfo.displayName}</h3>
            </div>
            {isActive ? (
              <div className="inline-flex items-center gap-2">
                <div className="size-3 rounded-full bg-green" />
                <span>{t('community.user_info.active_now')}</span>
              </div>
            ) : (
              !!userInfo.lastActiveAt && (
                <div className="inline-flex items-center gap-2">
                  <ActivityIcon />
                  <span className="text-base font-normal text-text-secondary">
                    {t('community.user_info.last_activity_at', {
                      date: lastActiveRelative,
                    })}
                  </span>
                </div>
              )
            )}
            {joinedRelative && isAuthorizedUser(user) && (
              <div className="inline-flex items-center gap-2">
                <CalendarIcon />
                <span className="text-base font-normal text-text-secondary">
                  {t('members.list.member.joined')} {joinedRelative}
                </span>
              </div>
            )}
            {!!userInfo?.email && (
              <div className="inline-flex items-center gap-2">
                <MailIcon />
                <a
                  href={`mailto:${userInfo.email}`}
                  className="text-base font-normal text-text-secondary"
                  title={userInfo.email}
                >
                  {userInfo.email}
                </a>
              </div>
            )}
            {!!userInfo?.bio && (
              <div className="flex flex-col items-start">
                <span className="max-w-[304px] text-base/6 font-normal wrap-break-word whitespace-normal text-text-secondary">
                  {getTruncatedText(userInfo.bio, isExpanded, MAX_VISIBLE_BIO_CHARS)}
                  {userInfo.bio.length > MAX_VISIBLE_BIO_CHARS && (
                    <button
                      className="text-base font-normal text-brand-primary"
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      ...
                      {isExpanded
                        ? t('community.user_info.hide.button')
                        : t('community.user_info.readmore.button')}
                    </button>
                  )}
                </span>
              </div>
            )}
            {userInfo?.contactId && (
              <ButtonLinkWrapper
                href={getContactUrl(userInfo.contactId)}
                target="_blank"
                rel="noreferrer"
              >
                <NeutralButton
                  className="w-full"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                  }}
                >
                  {t('community.user_info.open_contact_page')}
                </NeutralButton>
              </ButtonLinkWrapper>
            )}
          </div>
        </div>
      ) : (
        <UserInfoPopoverSkeleton />
      )}
    </PopoverPanel>
  )

  return (
    <Popover
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
      }}
      className={twMerge('truncate', className)}
    >
      {useNativeAnchor ? (
        <div className={'flex h-full items-center'}>
          {trigger}
          {show ? panel : null}
        </div>
      ) : (
        <Float
          show={show}
          offset={10}
          portal
          shift
          autoPlacement
          arrow
          enter="transition duration-100 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className={'flex h-full items-center'}
        >
          {trigger}
          {panel}
        </Float>
      )}
    </Popover>
  )
}
